'use client'
import { Button } from '@/components/ui/button'
import { useDictionary } from '@/contexts/DictionaryContext'
import { motion } from 'framer-motion'
import { ArrowRight, BarChart2, CheckCircle2, Cog, Database, Gauge, Globe, Layers, LayoutGrid, Network, RefreshCw, Settings, Zap } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRef } from 'react'
import AnimatedCounter from './AnimatedCounter'

enum Colors {
  blue = 'blue',
  green = 'green',
  purple = 'purple',
  orange = 'orange',
  red = 'red',
  teal = 'teal',
}

// Color mapping for different service types
const colorMap = {
  blue: {
    bg: 'bg-blue-600',
    bgLight: 'bg-blue-100',
    text: 'text-blue-600',
    border: 'border-blue-600',
    hover: 'hover:bg-blue-700',
    gradient: 'from-blue-600 to-blue-800',
  },
  green: {
    bg: 'bg-green-600',
    bgLight: 'bg-green-100',
    text: 'text-green-600',
    border: 'border-green-600',
    hover: 'hover:bg-green-700',
    gradient: 'from-green-600 to-green-800',
  },
  purple: {
    bg: 'bg-purple-600',
    bgLight: 'bg-purple-100',
    text: 'text-purple-600',
    border: 'border-purple-600',
    hover: 'hover:bg-purple-700',
    gradient: 'from-purple-600 to-purple-800',
  },
  orange: {
    bg: 'bg-orange-600',
    bgLight: 'bg-orange-100',
    text: 'text-orange-600',
    border: 'border-orange-600',
    hover: 'hover:bg-orange-700',
    gradient: 'from-orange-600 to-orange-800',
  },
  red: {
    bg: 'bg-red-600',
    bgLight: 'bg-red-100',
    text: 'text-red-600',
    border: 'border-red-600',
    hover: 'hover:bg-red-700',
    gradient: 'from-red-600 to-red-800',
  },
  teal: {
    bg: 'bg-teal-600',
    bgLight: 'bg-teal-100',
    text: 'text-teal-600',
    border: 'border-teal-600',
    hover: 'hover:bg-teal-700',
    gradient: 'from-teal-600 to-teal-800',
  },
}

export default function ServicesShowcase() {
  const containerRef = useRef(null)
  const { dictionary, locale } = useDictionary()

  const services = [
    {
      id: 'industrial-automation',
      title: dictionary.home.services.sections.industrial.title,
      subtitle: dictionary.home.services.sections.industrial.subtitle,
      description: dictionary.home.services.sections.industrial.description,
      image: '/assets/images/gallery/Mekanet (13).jpg',
      color: Colors.purple,
      icon: <Cog className='h-8 w-8' />,
      features: dictionary.home.services.sections.industrial.features,
      stats: [
        { value: dictionary.home.services.sections.industrial.stats.stat1.percentage, label: dictionary.home.services.sections.industrial.stats.stat1.title },
        { value: dictionary.home.services.sections.industrial.stats.stat2.percentage, label: dictionary.home.services.sections.industrial.stats.stat2.title },
        { value: dictionary.home.services.sections.industrial.stats.stat3.percentage, label: dictionary.home.services.sections.industrial.stats.stat3.title },
      ],
      technologies: [
        dictionary.home.services.sections.industrial.tags.tag1,
        dictionary.home.services.sections.industrial.tags.tag2,
        dictionary.home.services.sections.industrial.tags.tag3,
        dictionary.home.services.sections.industrial.tags.tag4,
      ],
      cta: {
        link: dictionary.home.services.sections.industrial.cta.link,
        text: dictionary.home.services.sections.industrial.cta.text,
      },
      subServices: [
        {
          icon: <Cog className='h-6 w-6' />,
          name: dictionary.home.services.sections.industrial.specialized.card1.title,
          description: dictionary.home.services.sections.industrial.specialized.card1.description,
        },
        {
          icon: <Globe className='h-6 w-6' />,
          name: dictionary.home.services.sections.industrial.specialized.card2.title,
          description: dictionary.home.services.sections.industrial.specialized.card2.description,
        },
        {
          icon: <Settings className='h-6 w-6' />,
          name: dictionary.home.services.sections.industrial.specialized.card3.title,
          description: dictionary.home.services.sections.industrial.specialized.card3.description,
        },
      ],
    },
    {
      id: 'turbo-machinery',
      title: dictionary.home.services.sections.turbo.title,
      subtitle: dictionary.home.services.sections.turbo.subtitle,
      description: dictionary.home.services.sections.turbo.description,
      image: '/assets/images/new/TURBO-ANASAYFA.jpg',
      color: Colors.orange,
      icon: <Gauge className='h-8 w-8' />,
      features: dictionary.home.services.sections.turbo.features,
      stats: [
        { value: dictionary.home.services.sections.turbo.stats.stat1.percentage, label: dictionary.home.services.sections.turbo.stats.stat1.title },
        { value: dictionary.home.services.sections.turbo.stats.stat2.percentage, label: dictionary.home.services.sections.turbo.stats.stat2.title },
        { value: dictionary.home.services.sections.turbo.stats.stat3.percentage, label: dictionary.home.services.sections.turbo.stats.stat3.title },
      ],
      technologies: [
        dictionary.home.services.sections.turbo.tags.tag1,
        dictionary.home.services.sections.turbo.tags.tag2,
        dictionary.home.services.sections.turbo.tags.tag3,
      ],
      cta: {
        link: dictionary.home.services.sections.turbo.cta.link,
        text: dictionary.home.services.sections.turbo.cta.text,
      },
      subServices: [
        {
          icon: <Settings className='h-6 w-6' />,
          name: dictionary.home.services.sections.turbo.specialized.card1.title,
          description: dictionary.home.services.sections.turbo.specialized.card1.description,
        },
        {
          icon: <BarChart2 className='h-6 w-6' />,
          name: dictionary.home.services.sections.turbo.specialized.card2.title,
          description: dictionary.home.services.sections.turbo.specialized.card2.description,
        },
        {
          icon: <Gauge className='h-6 w-6' />,
          name: dictionary.home.services.sections.turbo.specialized.card3.title,
          description: dictionary.home.services.sections.turbo.specialized.card3.description,
        },
      ],
    },
    {
      id: 'digital-transformation',
      title: dictionary.home.services.sections.digital.title,
      subtitle: dictionary.home.services.sections.digital.subtitle,
      description: dictionary.home.services.sections.digital.description,
      image: '/assets/images/new/dt-ana-sayfa-servis-ozet.jpeg',
      color: Colors.blue,
      icon: <LayoutGrid className='h-8 w-8' />,
      features: dictionary.home.services.sections.digital.features,
      stats: [
        { value: dictionary.home.services.sections.digital.stats.stat1.percentage, label: dictionary.home.services.sections.digital.stats.stat1.title },
        { value: dictionary.home.services.sections.digital.stats.stat2.percentage, label: dictionary.home.services.sections.digital.stats.stat2.title },
        { value: dictionary.home.services.sections.digital.stats.stat3.percentage, label: dictionary.home.services.sections.digital.stats.stat3.title },
      ],
      technologies: [
        dictionary.home.services.sections.digital.tags.tag1,
        dictionary.home.services.sections.digital.tags.tag2,
        dictionary.home.services.sections.digital.tags.tag3,
        dictionary.home.services.sections.digital.tags.tag4,
      ],
      cta: {
        link: dictionary.home.services.sections.digital.cta.link,
        text: dictionary.home.services.sections.digital.cta.text,
      },
      subServices: [
        {
          icon: <Layers className='h-6 w-6' />,
          name: dictionary.home.services.sections.digital.specialized.card1.title,
          description: dictionary.home.services.sections.digital.specialized.card1.description,
        },
        {
          icon: <Database className='h-6 w-6' />,
          name: dictionary.home.services.sections.digital.specialized.card2.title,
          description: dictionary.home.services.sections.digital.specialized.card2.description,
        },
        {
          icon: <Network className='h-6 w-6' />,
          name: dictionary.home.services.sections.digital.specialized.card3.title,
          description: dictionary.home.services.sections.digital.specialized.card3.description,
        },
      ],
    },
    {
      id: 'energy-management',
      title: dictionary.home.services.sections.energy.title,
      subtitle: dictionary.home.services.sections.energy.subtitle,
      description: dictionary.home.services.sections.energy.description,
      image: '/assets/images/new/ems-main-service-sum.jpeg',
      color: Colors.green,
      icon: <Zap className='h-8 w-8' />,
      features: dictionary.home.services.sections.energy.features,
      stats: [
        { value: dictionary.home.services.sections.energy.stats.stat1.percentage, label: dictionary.home.services.sections.energy.stats.stat1.title },
        { value: dictionary.home.services.sections.energy.stats.stat2.percentage, label: dictionary.home.services.sections.energy.stats.stat2.title },
        { value: dictionary.home.services.sections.energy.stats.stat3.percentage, label: dictionary.home.services.sections.energy.stats.stat3.title },
      ],
      technologies: [
        dictionary.home.services.sections.energy.tags.tag1,
        dictionary.home.services.sections.energy.tags.tag2,
        dictionary.home.services.sections.energy.tags.tag3,
        dictionary.home.services.sections.energy.tags.tag4,
      ],
      cta: {
        link: dictionary.home.services.sections.energy.cta.link,
        text: dictionary.home.services.sections.energy.cta.text,
      },
      subServices: [
        {
          icon: <BarChart2 className='h-6 w-6' />,
          name: dictionary.home.services.sections.energy.specialized.card1.title,
          description: dictionary.home.services.sections.energy.specialized.card1.description,
        },
        {
          icon: <RefreshCw className='h-6 w-6' />,
          name: dictionary.home.services.sections.energy.specialized.card2.title,
          description: dictionary.home.services.sections.energy.specialized.card2.description,
        },
        {
          icon: <Zap className='h-6 w-6' />,
          name: dictionary.home.services.sections.energy.specialized.card3.title,
          description: dictionary.home.services.sections.energy.specialized.card3.description,
        },
      ],
    },
    {
      id: 'condition',
      title: dictionary.home.services.sections.condition.title,
      subtitle: dictionary.home.services.sections.condition.subtitle,
      description: dictionary.home.services.sections.condition.description,
      image: '/assets/images/new/anasayfa-monitoring.jpeg',
      color: Colors.red,
      icon: <Cog className='h-8 w-8' />,
      features: dictionary.home.services.sections.condition.features,
      stats: [
        { value: dictionary.home.services.sections.condition.stats.stat1.percentage, label: dictionary.home.services.sections.condition.stats.stat1.title },
        { value: dictionary.home.services.sections.condition.stats.stat2.percentage, label: dictionary.home.services.sections.condition.stats.stat2.title },
        { value: dictionary.home.services.sections.condition.stats.stat3.percentage, label: dictionary.home.services.sections.condition.stats.stat3.title },
      ],
      technologies: [
        dictionary.home.services.sections.condition.tags.tag1,
        dictionary.home.services.sections.condition.tags.tag2,
        dictionary.home.services.sections.condition.tags.tag3,
        dictionary.home.services.sections.condition.tags.tag4,
      ],
      cta: {
        link: dictionary.home.services.sections.condition.cta.link,
        text: dictionary.home.services.sections.condition.cta.text,
      },
      subServices: [
        {
          icon: <Cog className='h-6 w-6' />,
          name: dictionary.home.services.sections.condition.specialized.card1.title,
          description: dictionary.home.services.sections.condition.specialized.card1.description,
        },
        {
          icon: <Globe className='h-6 w-6' />,
          name: dictionary.home.services.sections.condition.specialized.card2.title,
          description: dictionary.home.services.sections.condition.specialized.card2.description,
        },
        {
          icon: <Settings className='h-6 w-6' />,
          name: dictionary.home.services.sections.condition.specialized.card3.title,
          description: dictionary.home.services.sections.condition.specialized.card3.description,
        },
      ],
    },
    {
      id: 'predictive',
      title: dictionary.home.services.sections.predictive.title,
      subtitle: dictionary.home.services.sections.predictive.subtitle,
      description: dictionary.home.services.sections.predictive.description,
      image: '/assets/images/new/anasayfa-predictive.jpeg',
      color: Colors.teal,
      icon: <Gauge className='h-8 w-8' />,
      features: dictionary.home.services.sections.predictive.features,
      stats: [
        { value: dictionary.home.services.sections.predictive.stats.stat1.percentage, label: dictionary.home.services.sections.predictive.stats.stat1.title },
        { value: dictionary.home.services.sections.predictive.stats.stat2.percentage, label: dictionary.home.services.sections.predictive.stats.stat2.title },
        { value: dictionary.home.services.sections.predictive.stats.stat3.percentage, label: dictionary.home.services.sections.predictive.stats.stat3.title },
      ],
      technologies: [
        dictionary.home.services.sections.predictive.tags.tag1,
        dictionary.home.services.sections.predictive.tags.tag2,
        dictionary.home.services.sections.predictive.tags.tag3,
        dictionary.home.services.sections.predictive.tags.tag4,
      ],
      cta: {
        link: dictionary.home.services.sections.predictive.cta.link,
        text: dictionary.home.services.sections.predictive.cta.text,
      },
      subServices: [
        {
          icon: <Settings className='h-6 w-6' />,
          name: dictionary.home.services.sections.predictive.specialized.card1.title,
          description: dictionary.home.services.sections.predictive.specialized.card1.description,
        },
        {
          icon: <BarChart2 className='h-6 w-6' />,
          name: dictionary.home.services.sections.predictive.specialized.card2.title,
          description: dictionary.home.services.sections.predictive.specialized.card2.description,
        },
        {
          icon: <Gauge className='h-6 w-6' />,
          name: dictionary.home.services.sections.predictive.specialized.card3.title,
          description: dictionary.home.services.sections.predictive.specialized.card3.description,
        },
      ],
    },
  ]

  return (
    <section
      id='services'
      className='relative overflow-hidden py-20'
      ref={containerRef}
    >
      {/* Section header */}
      <div className='container mx-auto lg:px-4 md:px-2 px-1'>
        <motion.div
          className='mb-16 text-center'
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className='text-sm font-medium uppercase tracking-wider text-blue-400'>{dictionary.home.services.subtitle}</span>
          <h2 className='mt-2 text-4xl font-bold tracking-tight text-white md:text-5xl'>{dictionary.home.services.title}</h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-300'>{dictionary.home.services.description}</p>
        </motion.div>

        {/* Vertical service showcase */}
        <div className='space-y-24'>
          {services.map(service => {
            const colors = colorMap[service.color as keyof typeof colorMap]

            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6 }}
                className='relative rounded-2xl bg-slate-900 p-8 shadow-xl'
              >
                {/* Service accent line */}
                <div
                  className='absolute top-0 left-0 h-full w-2 rounded-l-2xl'
                  style={{ backgroundColor: `var(--${service.color}-600)` }}
                />

                {/* Service header */}
                <div className='mb-8 flex flex-col items-start sm:flex-row sm:items-center sm:justify-between'>
                  <div>
                    <div className={`mb-3 inline-flex rounded-full ${colors.bgLight} p-3 ${colors.text}`}>{service.icon}</div>
                    <span className={`block rounded-full px-3 py-1 text-xs font-medium ${colors.bgLight} ${colors.text} w-fit`}>{service.subtitle}</span>
                    <h3 className='mt-2 text-3xl font-bold text-white'>{service.title}</h3>
                  </div>
                  {service.cta.link !== '/services/predictive-maintenance' && service.cta.link !== '/services/condition-monitoring' && (
                    <Button
                      asChild
                      className={`mt-4 group ${colors.bg} ${colors.hover} text-white sm:mt-0`}
                    >
                      <Link href={service.cta.link}>
                        {service.cta.text}
                        <ArrowRight className='ml-2 h-4 w-4 transition-transform group-hover:translate-x-1' />
                      </Link>
                    </Button>
                  )}
                </div>

                {/* Service content */}
                <div className='grid grid-cols-1 gap-12 lg:grid-cols-2'>
                  {/* Content side */}
                  <div className='space-y-8'>
                    <p className='text-lg text-slate-300'>{service.description}</p>

                    <div className='space-y-4'>
                      <ul className='space-y-3'>
                        {service.features.map((feature: string, idx: number) => (
                          <motion.li
                            key={idx}
                            className='flex items-start'
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1, duration: 0.3 }}
                          >
                            <CheckCircle2 className={`mr-2 h-5 w-5 mt-0.5 ${colors.text}`} />
                            <span className='text-slate-300'>{feature}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>

                    <div className='grid grid-cols-3 gap-4'>
                      {service.stats.map((stat, idx) => (
                        <motion.div
                          key={idx}
                          className='rounded-lg bg-slate-800 p-4 text-center'
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.1, duration: 0.3 }}
                        >
                          <div className={`text-2xl font-bold ${colors.text}`}>
                            <AnimatedCounter
                              value={stat.value}
                              duration={2000}
                              formatter={value => value.toString()}
                            />
                          </div>
                          <div className='text-xs text-slate-400'>{stat.label}</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Image side */}
                  <div className='space-y-8'>
                    <div className='overflow-hidden shadow-lg'>
                      <Image
                        src={service.image || '/placeholder.svg'}
                        alt={service.title}
                        width={600}
                        height={400}
                        className='h-64 w-full object-cover transition-transform duration-500 hover:scale-105 lg:h-80'
                      />
                    </div>

                    <div className='space-y-4'>
                      <h4 className='text-xl font-semibold text-white'>
                        {locale === 'en' && 'Services'}
                        {locale === 'tr' && 'Hizmetler'}
                      </h4>
                      <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
                        {service.subServices.map((subService, idx) => (
                          <motion.div
                            key={idx}
                            className='rounded-lg bg-slate-800 p-4'
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 + idx * 0.1, duration: 0.3 }}
                          >
                            <div className={`mb-3 rounded-full ${colors.bgLight} p-2 inline-flex ${colors.text}`}>{subService.icon}</div>
                            <h5 className='mb-1 font-semibold text-white'>{subService.name}</h5>
                            <p className='text-sm text-slate-400'>{subService.description}</p>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
